import { Suspense, lazy, useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WorkflowProvider, useWorkflow } from './context/WorkflowContext';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import type { Estimate } from './types/items';
import { STEP_ORDER, Step } from './workflow/steps';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Account = lazy(() => import('./pages/Account'));
const Upload = lazy(() => import('./pages/Upload'));
const UploadReview = lazy(() => import('./pages/UploadReview'));
const FilesStepPage = lazy(() => import('./pages/FilesStepPage'));
const TradeRecapVariance = lazy(() => import('./pages/TradeRecapVariance'));
const Identify = lazy(() => import('./pages/Identify'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const Compare = lazy(() => import('./pages/Compare'));
const Packet = lazy(() => import('./pages/PacketPage'));
const QASessions = lazy(() => import('./pages/QASessions'));
const QADetail = lazy(() => import('./pages/QADetail'));
const ContentsInventoryToolPage = lazy(() => import('./pages/ContentsInventoryToolPage'));

const iconClass = 'h-5 w-5';

const UploadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 16V4" />
    <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
    <path d="M5 16.5v3a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5v-3" />
  </svg>
);

const DashboardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1.6" />
    <rect x="13" y="3.5" width="7.5" height="5.5" rx="1.6" />
    <rect x="13" y="10.5" width="7.5" height="9" rx="1.6" />
    <rect x="3.5" y="13" width="7.5" height="6.5" rx="1.6" />
  </svg>
);

const AccountIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className={iconClass}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="8.5" r="3.4" />
    <path d="M5 19.5c0-3.5 3.6-5.6 7-5.6s7 2.1 7 5.6" />
  </svg>
);

const ContentsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className={`${iconClass} text-amber-300`}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 7h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    <path d="M4 7l8 5 8-5" />
    <path d="M9 12h6" />
  </svg>
);

const RecapIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className={`${iconClass} text-amber-300`}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 20h16" />
    <path d="M7 16V9" />
    <path d="M12 16V6" />
    <path d="M17 16v-4" />
  </svg>
);

const APP_BASE = '/app';

const normalizeRecapLabel = (label?: string) =>
  String(label ?? '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const hasUnbalancedParens = (label: string) => {
  const openCount = (label.match(/\(/g) || []).length;
  const closeCount = (label.match(/\)/g) || []).length;
  return openCount !== closeCount;
};

const isSubtotalOrTotal = (label: string) => {
  const u = normalizeRecapLabel(label);
  if (!u) return true;
  if (u === 'TOTAL' || u.endsWith('SUBTOTAL')) return true;
  if (u.startsWith('COVERAGE:') || u.startsWith('COVERAGE ')) return true;
  if (u === 'RCV' || u === 'ACV' || u.startsWith('DEPREC')) return true;
  if (u.includes('DEDUCT')) return true;
  return false;
};

const shouldSkipRecapCategory = (label: string) =>
  !label || isSubtotalOrTotal(label) || hasUnbalancedParens(label);

const ADMIN_EMAIL = 'gallauerk@gmail.com';
const isAdminUser = (user: any) => {
  if (!user) return false;
  const email = String(user.email ?? user.username ?? '').trim().toLowerCase();
  return email === ADMIN_EMAIL;
};

const recapNumber = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const neg = trimmed.startsWith('(') && trimmed.endsWith(')');
    const cleaned = trimmed.replace(/[,$()]/g, '').trim();
    const num = Number(cleaned);
    if (!Number.isFinite(num)) return 0;
    return neg ? -num : num;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const extractRecapRows = (estimate?: Estimate) => {
  if (!estimate) return [];
  const parsedRaw = estimate.parsedRaw as Record<string, unknown> | undefined;
  const raw =
    estimate.recapByCategory ??
    (parsedRaw?.recapByCategory as unknown) ??
    ((parsedRaw?.summaryForDwelling as Record<string, unknown> | undefined)?.recapByCategory as unknown);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const category = String(
        (row as any).category ?? (row as any).trade ?? (row as any).name ?? '',
      ).trim();
      if (shouldSkipRecapCategory(category)) return null;
      return {
        category,
        rcv: recapNumber(
          (row as any).rcv ?? (row as any).RCV ?? (row as any).total ?? (row as any).Total ?? (row as any).amount,
        ),
      };
    })
    .filter(Boolean) as { category: string; rcv: number }[];
};

const sumRecapRcv = (estimate?: Estimate) =>
  extractRecapRows(estimate).reduce((acc, row) => acc + recapNumber(row?.rcv ?? 0), 0);

const BASE_SIDEBAR_ITEMS = [
  {
    path: APP_BASE,
    label: 'New Supplement',
    description: 'Import carrier & insured docs',
    icon: <UploadIcon />,
  },
  {
    path: `${APP_BASE}/dashboard`,
    label: 'Dashboard',
    description: 'Pipeline snapshot & metrics',
    icon: <DashboardIcon />,
  },
  {
    path: `${APP_BASE}/settings`,
    label: 'Settings',
    description: 'Company header for PDF exports',
    icon: <AccountIcon />,
  },
  {
    path: `${APP_BASE}/admin/users`,
    label: 'Admin Users',
    description: 'Account roster & access',
    icon: <AccountIcon />,
  },
  {
    path: '/divider-1',
    label: '',
    description: '',
    icon: null,
    divider: true,
  },
  {
    path: `${APP_BASE}/recap-variance`,
    label: 'Trade Recap Variance',
    description: 'Trade + room variance snapshot',
    icon: <RecapIcon />,
    hidePromptText: true,
  },
  {
    path: '/tools/contents-inventory',
    label: 'Contents Inventory Builder',
    description: '',
    icon: <ContentsIcon />,
    hidePromptText: true,
  },
];

const STEP_BY_PATH: Record<string, Step> = {
  '/': Step.UploadReview,
  '/room-mapping': Step.Packet,
  '/line-items': Step.Identify,
  '/match-items': Step.Identify,
  '/identify': Step.Identify,
  '/identify-build': Step.Identify,
  '/compare': Step.Identify,
  '/packet': Step.Packet,
  '/app': Step.UploadReview,
  '/app/room-mapping': Step.Packet,
  '/app/line-items': Step.Identify,
  '/app/match-items': Step.Identify,
  '/app/identify': Step.Identify,
  '/app/identify-build': Step.Identify,
  '/app/packet': Step.Packet,
};

const STEP_LABELS: Record<Step, string> = {
  [Step.UploadReview]: 'Upload',
  [Step.RecapVariance]: 'Recap Variance',
  [Step.Identify]: 'Identify & Build',
  [Step.Missing]: 'Missing',
  [Step.Opportunities]: 'Opportunities',
  [Step.Packet]: 'Packet',
};

const PATH_BY_STEP: Record<Step, string> = {
  [Step.UploadReview]: `${APP_BASE}`,
  [Step.RecapVariance]: `${APP_BASE}/recap-variance`,
  [Step.Identify]: `${APP_BASE}/identify-build`,
  [Step.Missing]: `${APP_BASE}/missing`,
  [Step.Opportunities]: `${APP_BASE}/opportunities`,
  [Step.Packet]: `${APP_BASE}/packet`,
};

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { step, setStep, left, right, wizardData } = useWorkflow();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem('cv.justLoggedIn') === '1';
  });
  const [workflowHeaderCollapsed, setWorkflowHeaderCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem('cv.justLoggedIn') === '1';
  });
  const [lastSupplementPath, setLastSupplementPath] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem('cv.lastSupplementPath') || '';
  });
  const [roomListPromptVisible, setRoomListPromptVisible] = useState(false);
  const roomListPromptTimerRef = useRef<number | null>(null);
  const entranceTimerRef = useRef<number | null>(null);
  const currentYear = new Date().getFullYear();
  const isToolsShell = location.pathname.startsWith('/tools');
  const isWorkflowHeaderCollapsed = !isToolsShell && workflowHeaderCollapsed;
  const entranceDelayMs = 350;
  const recapReady = Boolean(left?.sessionId && right?.sessionId);
  const carrierEstimate = left;
  const insuredEstimate = right;
  const carrierRecapTotal = sumRecapRcv(carrierEstimate);
  const insuredRecapTotal = sumRecapRcv(insuredEstimate);
  const recapHasTotals = carrierRecapTotal > 0 || insuredRecapTotal > 0;
  const recapNet =
    recapReady && recapHasTotals ? insuredRecapTotal - carrierRecapTotal : null;
  const formatMoney = (value: number) =>
    value.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  const recapPrompt = recapReady
    ? (() => {
        const parts = ['Recap variance ready'];
        if (typeof recapNet === 'number' && Number.isFinite(recapNet) && Math.abs(recapNet) >= 1) {
          const sign = recapNet >= 0 ? '+' : '-';
          parts.push(`Net ${sign}${formatMoney(Math.abs(recapNet))}`);
        }
        return parts.join(' | ');
      })()
    : undefined;
  const lockedNote = 'Upload both estimates to unlock';
  const isAdmin = isAdminUser(user);
  const sidebarItems = BASE_SIDEBAR_ITEMS.filter(
    (item) => item.path !== `${APP_BASE}/admin/users` || isAdmin,
  ).map((item) => {
    if (item.path === `${APP_BASE}/recap-variance`) {
      return {
        ...item,
        disabled: !recapReady,
        disabledReason: recapReady ? undefined : lockedNote,
      };
    }
    return item;
  });
  const stepIndex = STEP_ORDER.indexOf(step);
  const prevStep = stepIndex > 0 ? STEP_ORDER[stepIndex - 1] : null;
  const nextStep = stepIndex >= 0 && stepIndex < STEP_ORDER.length - 1 ? STEP_ORDER[stepIndex + 1] : null;
  const handlePrevStep = useCallback(() => {
    if (!prevStep) return;
    setStep(prevStep);
    const targetPath = PATH_BY_STEP[prevStep];
    if (targetPath) navigate(targetPath);
  }, [navigate, prevStep, setStep]);
  const handleNextStep = useCallback(() => {
    if (!nextStep) return;
    setStep(nextStep);
    const targetPath = PATH_BY_STEP[nextStep];
    if (targetPath) navigate(targetPath);
  }, [navigate, nextStep, setStep]);
  const handleWorkflowHeaderClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (isToolsShell) return;
      const target = event.target;
      if (target instanceof Element) {
        const isInteractive = target.closest(
          'button, a, input, select, textarea, [role="button"]',
        );
        if (isInteractive) return;
      }
      setWorkflowHeaderCollapsed((prev) => !prev);
    },
    [isToolsShell],
  );
  const headerStyle = {
    backgroundColor: isWorkflowHeaderCollapsed ? 'transparent' : '#020617',
    backgroundImage: isWorkflowHeaderCollapsed
      ? 'none'
      : 'linear-gradient(135deg, rgba(2,6,23,0.95) 0%, rgba(8,12,24,0.9) 40%, rgba(15,23,42,0.75) 70%, rgba(2,6,23,0.6) 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
  const isUploadRoot =
    !isToolsShell && (location.pathname === APP_BASE || location.pathname === `${APP_BASE}/`);
  const mainPaddingClass = isUploadRoot
    ? 'px-6 pb-4 lg:px-12 lg:pb-6'
    : 'px-6 pb-8 lg:px-12 lg:pb-10';
  const supplementPathPrefixes = [
    `${APP_BASE}/identify-build`,
    `${APP_BASE}/identify`,
    `${APP_BASE}/compare`,
    `${APP_BASE}/packet`,
    `${APP_BASE}/missing`,
    `${APP_BASE}/opportunities`,
    `${APP_BASE}/files`,
    `${APP_BASE}/upload-legacy`,
    `${APP_BASE}/line-items`,
    `${APP_BASE}/match-items`,
    `${APP_BASE}/room-mapping`,
  ];
  const isSupplementPath =
    location.pathname === APP_BASE ||
    location.pathname === `${APP_BASE}/` ||
    supplementPathPrefixes.some((prefix) => location.pathname.startsWith(prefix));
  const isIdentifyBuildPath =
    !isToolsShell && location.pathname.startsWith(`${APP_BASE}/identify-build`);
  const isRecapVariancePath =
    !isToolsShell && location.pathname.startsWith(`${APP_BASE}/recap-variance`);
  const isContentsInventoryPath = location.pathname.startsWith('/tools/contents-inventory');

  useEffect(() => {
    if (!isIdentifyBuildPath) return;
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem('cv.justLoggedIn') === '1'
    ) {
      return;
    }
    setCollapsed(true);
    setWorkflowHeaderCollapsed(true);
  }, [isIdentifyBuildPath]);

  useEffect(() => {
    if (!isRecapVariancePath) return;
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem('cv.justLoggedIn') === '1'
    ) {
      return;
    }
    setCollapsed(true);
    setWorkflowHeaderCollapsed(false);
  }, [isRecapVariancePath]);

  useEffect(() => {
    if (!isContentsInventoryPath) return;
    setCollapsed(true);
  }, [isContentsInventoryPath]);

  useEffect(() => {
    if (isToolsShell) return;
    if (typeof window === 'undefined') return;
    const justLoggedIn = window.sessionStorage.getItem('cv.justLoggedIn') === '1';
    if (!justLoggedIn) return;

    setCollapsed(true);
    setWorkflowHeaderCollapsed(true);

    if (entranceTimerRef.current) {
      window.clearTimeout(entranceTimerRef.current);
    }
    entranceTimerRef.current = window.setTimeout(() => {
      setCollapsed(false);
      setWorkflowHeaderCollapsed(false);
      entranceTimerRef.current = null;
      window.sessionStorage.removeItem('cv.justLoggedIn');
    }, entranceDelayMs);

    return () => {
      if (entranceTimerRef.current) {
        window.clearTimeout(entranceTimerRef.current);
        entranceTimerRef.current = null;
      }
    };
  }, [entranceDelayMs, isToolsShell]);

  useEffect(() => {
    if (isToolsShell) return;
    if (isRecapVariancePath) return;
    const normalizePathname = (pathname: string) => {
      if (pathname.startsWith(APP_BASE)) {
        const remainder = pathname.slice(APP_BASE.length) || '/';
        return remainder.startsWith('/') ? remainder : `/${remainder}`;
      }
      return pathname;
    };
    const normalizedPath = normalizePathname(location.pathname);
    let normalized = STEP_BY_PATH[normalizedPath];
    if (!normalized && normalizedPath.startsWith('/compare')) {
      normalized = Step.Identify;
    }
    if (!normalized) {
      normalized = Step.UploadReview;
    }
    if (normalized !== step) {
      setStep(normalized);
    }
  }, [location.pathname, isToolsShell, isRecapVariancePath, step, setStep]);

  useEffect(() => {
    if (isToolsShell) return;
    if (!isSupplementPath) return;
    const fullPath = `${location.pathname}${location.search || ''}`;
    setLastSupplementPath(fullPath);
    try {
      window.localStorage.setItem('cv.lastSupplementPath', fullPath);
    } catch {
      /* ignore */
    }
  }, [isToolsShell, isSupplementPath, location.pathname, location.search]);

  const activeIndex = STEP_ORDER.findIndex((entry) => entry === step);
  const roomListReady = Boolean(
    left?.parsedRaw &&
      right?.parsedRaw &&
      ((wizardData?.carrierRooms?.length ?? 0) + (wizardData?.insuredRooms?.length ?? 0) > 0 ||
        (left?.rooms?.length ?? 0) + (right?.rooms?.length ?? 0) > 0),
  );
  const sidebarPrompts = (() => {
    const prompts: Record<string, string> = {};
    if (roomListPromptVisible) {
      prompts['/tools/contents-inventory'] = 'Upload estimate for room list to populate';
    }
    if (recapPrompt) {
      prompts[`${APP_BASE}/recap-variance`] = recapPrompt;
    }
    return Object.keys(prompts).length ? prompts : undefined;
  })();
  const resumeAfterPath = (() => {
    const match = sidebarItems.find((item) => {
      if (item.divider) return false;
      if (location.pathname === item.path) return true;
      return location.pathname.startsWith(`${item.path}/`);
    });
    return match?.path || APP_BASE;
  })();
  const resumeSupplement =
    !isSupplementPath && lastSupplementPath
      ? {
          label: 'Continue Supplement',
          afterPath: resumeAfterPath,
          onClick: () => navigate(lastSupplementPath),
        }
      : undefined;

  useEffect(() => {
    if (!roomListReady) {
      setRoomListPromptVisible(false);
      if (roomListPromptTimerRef.current) {
        window.clearTimeout(roomListPromptTimerRef.current);
        roomListPromptTimerRef.current = null;
      }
      return;
    }

    setRoomListPromptVisible(true);
    if (roomListPromptTimerRef.current) {
      window.clearTimeout(roomListPromptTimerRef.current);
    }
    roomListPromptTimerRef.current = window.setTimeout(() => {
      setRoomListPromptVisible(false);
      roomListPromptTimerRef.current = null;
    }, 30000);

    return () => {
      if (roomListPromptTimerRef.current) {
        window.clearTimeout(roomListPromptTimerRef.current);
        roomListPromptTimerRef.current = null;
      }
    };
  }, [roomListReady, left?.sessionId, right?.sessionId]);

  const handleStepClick = useCallback(
    (targetStep: Step, index: number) => {
      if (index >= activeIndex) return;
      const targetPath = PATH_BY_STEP[targetStep];
      if (!targetPath) return;
      setStep(targetStep);
      navigate(targetPath);
    },
    [activeIndex, navigate, setStep],
  );

  return (
    <div
      className="flex min-h-screen text-slate-900"
      style={{
        backgroundColor: '#d1d5db',
      }}
    >
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((prev) => !prev)}
        items={sidebarItems}
        activePath={location.pathname}
        promptsByPath={sidebarPrompts}
        resumeSupplement={resumeSupplement}
      />
      <div className="flex-1 bg-white text-slate-900 shadow-inner">
        <header
          className={[
            'border-b overflow-hidden transition-[border-color,box-shadow,background-color] duration-[1400ms] ease-[cubic-bezier(0.45,0,0.55,1)]',
            isWorkflowHeaderCollapsed
              ? 'border-transparent text-slate-700 shadow-none'
              : 'border-white/5 text-white shadow-lg',
            isToolsShell ? '' : 'cursor-pointer',
          ].join(' ')}
          style={headerStyle}
          onClick={handleWorkflowHeaderClick}
        >
          <div className="relative">
            <div
              className={[
                'grid transition-[grid-template-rows,opacity] duration-[1400ms] ease-[cubic-bezier(0.45,0,0.55,1)]',
                isWorkflowHeaderCollapsed
                  ? 'grid-rows-[0fr] opacity-0 pointer-events-none'
                  : 'grid-rows-[1fr] opacity-100',
              ].join(' ')}
              aria-hidden={isWorkflowHeaderCollapsed}
            >
              <div className="overflow-hidden">
                <div
                  className={[
                    'flex flex-wrap items-center gap-4 px-8 py-5',
                    isWorkflowHeaderCollapsed ? '' : 'workflow-expand-pulse',
                  ].join(' ')}
                >
                <div className="min-w-[240px]">
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-[0.4em] text-slate-400">ClaimSupplement</p>
                    <h1 className="text-2xl font-semibold text-white">
                      {isToolsShell ? 'Contents Inventory' : 'Workflow Experience'}
                    </h1>
                  </div>
                  <p className="text-sm text-slate-300">
                    {isToolsShell
                      ? 'Standalone contents inventory tool'
                      : 'Side-by-side auditing + packet export'}
                  </p>
                </div>
                {!isToolsShell && (
                  <div className="order-last w-full lg:order-none lg:flex-1">
                    <div className="flex flex-wrap items-center justify-start gap-2 pl-4 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                      {STEP_ORDER.map((workflowStep, idx) => {
                        const complete = idx <= activeIndex;
                        return (
                          <div key={workflowStep} className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleStepClick(workflowStep, idx)}
                              disabled={idx >= activeIndex}
                              className={`flex items-center gap-2 rounded-full px-1 py-0.5 transition ${
                                idx < activeIndex ? 'cursor-pointer hover:text-white' : 'cursor-default'
                              }`}
                              aria-disabled={idx >= activeIndex}
                            >
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  complete ? 'bg-teal-300' : 'bg-slate-700'
                                }`}
                              />
                              <span className={complete ? 'text-white' : ''}>
                                {STEP_LABELS[workflowStep]}
                              </span>
                            </button>
                            {idx < STEP_ORDER.length - 1 && <span className="h-px w-8 bg-white/10" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {!isToolsShell && (
                  <div className="min-w-[140px] text-right">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setWorkflowHeaderCollapsed((prev) => !prev)}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white"
                        aria-expanded={!isWorkflowHeaderCollapsed}
                        aria-label={isWorkflowHeaderCollapsed ? 'Expand workflow header' : 'Collapse workflow header'}
                        title={isWorkflowHeaderCollapsed ? 'Expand workflow header' : 'Collapse workflow header'}
                      >
                        <span className="sr-only">
                          {isWorkflowHeaderCollapsed ? 'Expand workflow header' : 'Collapse workflow header'}
                        </span>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={`h-3 w-3 transition-transform ${
                            isWorkflowHeaderCollapsed ? 'rotate-180' : 'rotate-0'
                          }`}
                          aria-hidden="true"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">Current step</div>
                    <div className="mt-0.5 text-[12px] font-semibold text-white">
                      {STEP_LABELS[step] || 'Upload'}
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
            <div
              className={[
                'grid transition-[grid-template-rows,opacity] duration-[1400ms] ease-[cubic-bezier(0.45,0,0.55,1)]',
                isWorkflowHeaderCollapsed
                  ? 'grid-rows-[1fr] opacity-100'
                  : 'grid-rows-[0fr] opacity-0 pointer-events-none',
              ].join(' ')}
              aria-hidden={!isWorkflowHeaderCollapsed}
            >
              <div className="overflow-hidden">
                <div className="flex items-center justify-end px-8 py-2">
                  <button
                    type="button"
                    onClick={() => setWorkflowHeaderCollapsed((prev) => !prev)}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                    aria-expanded={!isWorkflowHeaderCollapsed}
                    aria-label={isWorkflowHeaderCollapsed ? 'Expand workflow header' : 'Collapse workflow header'}
                    title={isWorkflowHeaderCollapsed ? 'Expand workflow header' : 'Collapse workflow header'}
                  >
                    <span className="sr-only">
                      {isWorkflowHeaderCollapsed ? 'Expand workflow header' : 'Collapse workflow header'}
                    </span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`h-3 w-3 transition-transform ${isWorkflowHeaderCollapsed ? 'rotate-180' : 'rotate-0'}`}
                      aria-hidden="true"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>
        <main
          className={[
            mainPaddingClass,
            isWorkflowHeaderCollapsed ? 'pt-0 lg:pt-0' : 'pt-6 lg:pt-8',
            'transition-[padding] duration-[1400ms] ease-[cubic-bezier(0.45,0,0.55,1)]',
          ].join(' ')}
        >
          <div
            className={[
              'relative min-h-[70vh] overflow-hidden transition-[padding,box-shadow,border-radius] duration-[1400ms] ease-[cubic-bezier(0.45,0,0.55,1)]',
              'border border-white/60 bg-white/80 p-6 backdrop-blur-lg lg:p-8',
              isWorkflowHeaderCollapsed
                ? 'rounded-b-3xl pt-3 shadow-none lg:pt-4'
                : 'rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.2)]',
            ].join(' ')}
          >
            <Suspense fallback={<div className="text-sm text-slate-500">Loading...</div>}>
              <Routes>
                {isToolsShell ? (
                  <>
                    <Route index element={<Navigate to="contents-inventory" replace />} />
                    <Route path="contents-inventory" element={<ContentsInventoryToolPage />} />
                    <Route path="*" element={<Navigate to="contents-inventory" replace />} />
                  </>
                ) : (
                  <>
                    <Route index element={<Upload />} />
                    <Route path="recap-variance" element={<TradeRecapVariance />} />
                    <Route path="files" element={<FilesStepPage />} />
                    <Route path="upload-legacy" element={<UploadReview />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="account" element={<Account />} />
                    <Route path="settings" element={<AccountSettings />} />
                    <Route
                      path="admin/users"
                      element={isAdmin ? <AdminUsers /> : <Navigate to="/app" replace />}
                    />
                    <Route path="room-mapping" element={<Navigate to="/app/packet" replace />} />
                    <Route path="line-items" element={<Navigate to="/app/identify-build" replace />} />
                    <Route path="match-items" element={<Navigate to="/app/identify-build" replace />} />
                    <Route path="identify" element={<Navigate to="/app/identify-build" replace />} />
                    <Route path="identify-build" element={<Identify />} />
                    <Route path="compare/:sessionId?" element={<Compare />} />
                    <Route path="packet" element={<Packet />} />
                    <Route path="qa" element={<QASessions />} />
                    <Route path="qa/:id" element={<QADetail />} />
                    <Route path="*" element={<Navigate to="/app" replace />} />
                  </>
                )}
              </Routes>
            </Suspense>
          </div>
        </main>
        <footer className="px-6 pb-10 text-xs text-slate-500 lg:px-12">
          <div className="mx-auto flex w-full max-w-6xl justify-center text-center">
            © {currentYear} ClaimSupplement.ai. All rights reserved.
          </div>
        </footer>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <WorkflowProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <Shell />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/*"
            element={
              <ProtectedRoute>
                <Shell />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </WorkflowProvider>
    </AuthProvider>
  );
}
